import React, {ReactNode, useEffect} from 'react';
import classNames from 'classnames';
import type {DraggableSyntheticListeners} from '@dnd-kit/core';
import type {Transform} from '@dnd-kit/utilities';
import styles from './Item.module.scss';
import {allowMouseDownEvent} from './utils';
import {noop} from '@priority-software/priority-style-react';

export interface Props {
    dragOverlay?: boolean;
    color?: string;
    disabled?: boolean;
    dragging?: boolean;
    handle?: boolean;
    handleProps?: any;
    height?: number;
    fadeIn?: boolean;
    transform?: Transform | null;
    listeners?: DraggableSyntheticListeners;
    sorting?: boolean;
    style?: React.CSSProperties;
    transition?: string | null;
    wrapperStyle?: React.CSSProperties;
    value: React.ReactNode;
    children?: ReactNode;
    isActive?: boolean;
}

export const Item = React.memo(
    React.forwardRef<HTMLLIElement, Props>(
        (
            {
                color,
                dragOverlay,
                dragging,
                disabled,
                fadeIn,
                handle,
                handleProps,
                height,
                listeners,
                sorting,
                style,
                transition,
                transform,
                value,
                wrapperStyle,
                children,
                isActive,
                ...props
            },
            ref
        ) => {
            useEffect(() => {
                if (!dragOverlay) return noop;

                document.body.style.cursor = 'grabbing';

                return () => {
                    document.body.style.cursor = '';
                };
            }, [dragOverlay]);

            const listenersData = {
                ...listeners,
                onMouseDown: (event: React.MouseEvent) => {
                    if (
                        allowMouseDownEvent(
                            event.currentTarget as HTMLElement,
                            event.target as HTMLElement
                        )
                    ) {
                        listeners.onMouseDown?.(event);
                    }
                }
            };

            return (
                <div
                    className={classNames(styles.Wrapper, {
                        [styles.fadeIn]: fadeIn,
                        [styles.dragOverlay]: dragOverlay,
                        'pui-homepage-item-active': isActive
                    })}
                    style={
                        {
                            ...wrapperStyle,
                            transition: [transition, wrapperStyle?.transition]
                                .filter(Boolean)
                                .join(', '),
                            '--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
                            '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
                            '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
                            '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined
                        } as React.CSSProperties
                    }
                    ref={ref as any}
                    id={value as string}
                >
                    <div
                        className={classNames(
                            styles.Item,
                            dragging && styles.dragging,
                            handle && styles.withHandle,
                            dragOverlay && styles.dragOverlay
                        )}
                        style={style}
                        {...(!handle && !disabled ? listenersData : undefined)}
                        {...props}
                    >
                        {children || value}
                    </div>
                </div>
            );
        }
    )
);
